import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '@environment';
import {
  isLegacySegmentedMessage,
  resolveSuccessMessageFormat,
  SUCCESS_MESSAGE_FORMAT,
} from '@gs-2.0/core';
import { Step } from '@gs-2.0/shared';
import { PaymentService } from '../../../_core/api/payment.service';
import { TransactionService } from '../../../_core/api/transaction.service';
import { CONTRIBUTION_TYPE } from '../../../_core/constants/Contribute';
import { COUNTRY_NAMES } from '../../../_core/constants/country-names';
import { EnvironmentNames } from '../../../_core/constants/environments';
import FundTypes from '../../../_core/constants/fund-types';
import PageTitles from '../../../_core/constants/page-titles';
import { PaymentStatus } from '../../../_core/constants/Payment';
import Urls from '../../../_core/constants/urls';
import {
  WIRING_INSTRUCTIONS_AUSTRALIA,
  WIRING_INSTRUCTIONS_CNCF,
  WIRING_INSTRUCTIONS_FONTE,
  WIRING_INSTRUCTIONS_GAUDIUM,
  WIRING_INSTRUCTIONS_GLF,
  WIRING_INSTRUCTIONS_IGF,
  WIRING_INSTRUCTIONS_NEW_ZEALAND,
  WIRING_INSTRUCTIONS_SINNGEBER,
  WIRING_INSTRUCTIONS_SWISS,
  WIRING_INSTRUCTIONS_TOL,
} from '../../../_core/constants/wiring-instructions';
import StorageHelper from '../../../_core/helpers/storage.helper';
import { PaymentResponse, PaymentResponseDto } from '../../../_core/models/PaymentResponse';
import { SelectedActivityFundService } from '../../../_core/services/selected-activity-fund.service';
import { TitleService } from '../../../_core/services/title.service';
import { FundNameFormatPipe } from '../../../_shared/pipes/fund-name-format.pipe';
import { SearchCountry } from '../donate-form/donate-form.model';

@Component({
  selector: 'app-payment',
  templateUrl: './post-payment.component.html',
  styleUrls: ['./post-payment.component.scss'],
  standalone: false,
})
export class PostPaymentComponent implements OnInit {
  paymentStatus = PaymentStatus;
  url = Urls;
  environments = EnvironmentNames;
  contributionTypes = CONTRIBUTION_TYPE;
  paymentData: PaymentResponse;
  wiringInstructions: any[];
  environmentName: string;
  email: string;
  tenantName: string;
  confirmed: boolean;
  canceled: boolean;
  public: boolean;
  loading: boolean;
  countryNames = COUNTRY_NAMES;
  isSwiss: boolean;
  isTol: boolean;
  homeLink: string;
  country: string;
  networkMemberName: string;
  customLink: string;
  customLinkFromRedirect: boolean = false;
  gsUrl: string;
  utmSource: string;
  signature: string;
  isPuertoRico: boolean;
  networkMemberData: SearchCountry | null = null;
  loadingCountryDetails: boolean = false;
  isPayOffline: boolean;
  private readonly AUSGIVING_MAX_AMOUNT = 100000;

  // Stepper data
  steps: Step[] = [
    { label: 'contribute.stepper.details', number: 1 },
    { label: 'contribute.stepper.summary', number: 2 },
    {
      label: 'contribute.stepper.confirmation',
      mobileLabel: 'contribute.stepper.confirm',
      number: 3,
    },
  ];
  currentStep = 2; // Confirmation step (0-indexed)

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private paymentService: PaymentService,
    private titleService: TitleService,
    private selectedActivityFund: SelectedActivityFundService,
    private transactionService: TransactionService,
    private fundNameFormatPipe: FundNameFormatPipe
  ) {
    this.environmentName = environment.fund.environmentName;
    this.email = environment.fund.mailTo;
    this.tenantName = environment.fund.name;
  }

  ngOnInit(): void {
    this.confirmed = this.router.url.includes(`/${Urls.CONFIRM}`);
    this.canceled = this.router.url.includes(`/${Urls.CANCEL}`);
    const title = this.confirmed ? PageTitles.THANK_YOU : PageTitles.PAYMENY_FAILED;
    this.titleService.titleChanged(title);
    const isPublicDonate = this.router.url.includes(`/${Urls.PUBLIC}/${Urls.DONATE}`);
    this.public = isPublicDonate && (this.confirmed || this.canceled);
    this.isSwiss = environment.fund.environmentName === EnvironmentNames.SWISS;
    this.isTol = environment.fund.environmentName === EnvironmentNames.TOL;
    this.handleQueryParams();
    this.isPuertoRico = this.countryNames.PUERTO_RICO.toUpperCase() === this.country?.toUpperCase();
  }

  handleQueryParams(): void {
    const queryParams = this.route.snapshot.queryParams;
    this.utmSource = queryParams.utmSource || null;
    this.signature = queryParams.signature || null;
    this.customLink = this.getCustomLink();
    this.isPayOffline = queryParams.isPayOffline ? true : false;
    this.homeLink = this.gsUrl
      ? environment.fund.gsUrl
      : StorageHelper.getSessionStorageItem('homeLink') || queryParams.homeLink;
    this.gsUrl = queryParams.gsUrl || StorageHelper.getSessionStorageItem('gsUrl');
    if (queryParams.sessionId) {
      this.getPurchaseStatus(queryParams.sessionId, queryParams?.recurring);
    } else {
      this.paymentData = new PaymentResponse(queryParams, this.isSwiss, this.isTol);
      if (this.paymentData.shouldDisplayBankInstructions) this.setWiringInstructions();
    }
    this.country = queryParams.countryName || null;

    // Fetch country details if countryUuid is available
    if (queryParams.countryUuid) {
      this.getCountryDetailsData(queryParams.countryUuid);
    }
  }

  getPurchaseStatus(sessionId: string, recurring = false): void {
    this.loading = true;
    this.paymentService.getPurchaseStatus(sessionId, this.confirmed, recurring).subscribe({
      next: (res: PaymentResponseDto) => {
        this.paymentData = new PaymentResponse(res, this.isSwiss, this.isTol);
        this.router.navigate([], {
          queryParams: {
            ...this.paymentData,
            homeLink: this.homeLink,
          },
          replaceUrl: true,
        });
        this.loading = false;
      },
      error: () => {
        this.paymentData = new PaymentResponse();
        this.paymentData.status = PaymentStatus.FAILED;
        this.paymentData.contributionType = CONTRIBUTION_TYPE.CREDIT_CARD;
        this.loading = false;
      },
    });
  }

  setWiringInstructions(): void {
    switch (environment.fund.environmentName) {
      case EnvironmentNames.IGF:
        this.wiringInstructions = WIRING_INSTRUCTIONS_IGF;
        break;
      case EnvironmentNames.SWISS:
        const updatedSwiss = WIRING_INSTRUCTIONS_SWISS.filter((instructions) =>
          typeof instructions.taxDeduction === 'boolean'
            ? instructions.taxDeduction ===
              (this.paymentData.fundType === FundTypes.DAF ? true : this.paymentData.taxDeduction)
            : true
        );

        const allSwissInstructions = [
          ...updatedSwiss,
          ...WIRING_INSTRUCTIONS_NEW_ZEALAND,
          ...WIRING_INSTRUCTIONS_AUSTRALIA,
        ];
        this.wiringInstructions = [
          allSwissInstructions.find(
            (instructionsSet) => instructionsSet.currency === this.paymentData.currencySymbol
          ),
        ];
        break;
      case EnvironmentNames.GLF:
        this.wiringInstructions = WIRING_INSTRUCTIONS_GLF;
        break;
      case EnvironmentNames.CNCF:
        this.wiringInstructions = WIRING_INSTRUCTIONS_CNCF;
        break;
      case EnvironmentNames.SINNGEBER:
        this.wiringInstructions = [...WIRING_INSTRUCTIONS_SINNGEBER];
        delete this.wiringInstructions[0].currency;
        break;
      case EnvironmentNames.TOL:
        this.wiringInstructions = [
          this.paymentData.taxDeduction
            ? [...WIRING_INSTRUCTIONS_TOL][0]
            : [...WIRING_INSTRUCTIONS_TOL][1],
        ];
        delete this.wiringInstructions[0].currency;
        break;
      case EnvironmentNames.GES:
        this.wiringInstructions = WIRING_INSTRUCTIONS_GAUDIUM;
        break;
      case EnvironmentNames.FONTE:
        this.wiringInstructions = WIRING_INSTRUCTIONS_FONTE;
        break;
    }
  }

  private getTrackingQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (this.utmSource) params['utm_source'] = this.utmSource;
    if (this.signature) params['signature'] = this.signature;
    return params;
  }

  goTo(url: Urls): void {
    this.customLink = this.getCustomLink();

    if (url === Urls.DONATE) {
      const publicDonate = `${Urls.PUBLIC}/${Urls.DONATE}`;
      const queryParams = {
        ...(this.customLink ? { id: this.customLink } : {}),
        ...this.getTrackingQueryParams(),
      };
      const qp = Object.keys(queryParams).length ? queryParams : undefined;

      this.paymentData.wasLoggedIn || !this.public
        ? this.router.navigate([`${Urls.FUND}/${Urls.DONATE}`], { queryParams: qp })
        : this.router.navigate([publicDonate], { queryParams: qp });
    } else if (url === Urls.ACTIVITY) {
      if (!this.gsUrl || this.gsUrl === 'undefined') {
        this.selectedActivityFund.changeActivityFund(this.paymentData.cseg5);
        this.router.navigate([`${Urls.FUND}/${Urls.ACTIVITY}`]);
      } else {
        const gsPath = `${this.gsUrl}/${Urls.ACTIVITY}`;
        window.open(gsPath, '_self');
      }
    } else if (url === Urls.MANAGE_RECURRING) {
      if (!this.gsUrl || this.gsUrl === 'undefined') {
        this.router.navigate([`${Urls.FUND}/${Urls.DONATE}/${url}`]);
      } else {
        const gsPath = `${this.gsUrl}/${Urls.DONATE}/${url}`;
        window.open(gsPath, '_self');
      }
    } else if (this.paymentData.redirectLink && (!this.gsUrl || this.gsUrl === 'undefined')) {
      const base = this.paymentData.redirectLink;
      const queryParams = {
        ...(this.customLink && !this.customLinkFromRedirect ? { id: this.customLink } : {}),
        ...this.getTrackingQueryParams(),
      };
      const queryString = new URLSearchParams(queryParams).toString();
      const redirectLink = queryString ? `${base}?${queryString}` : base;
      window.open(redirectLink, '_self');
    } else {
      const base = `${Urls.PUBLIC}/${url}`;
      const queryParams = {
        ...(this.customLink ? { id: this.customLink } : {}),
        ...this.getTrackingQueryParams(),
      };
      const queryString = new URLSearchParams(queryParams).toString();
      const path = queryString ? `${base}?${queryString}` : base;

      if (this.gsUrl && this.gsUrl !== 'undefined') {
        const redirectLink = new URL(this.paymentData.redirectLink);
        const giveLinkHash = redirectLink.search || '';
        const gsPath = `${this.gsUrl}/${path}${giveLinkHash}`;
        window.open(gsPath, '_self');
      } else {
        this.router.navigateByUrl(path);
      }
    }
  }

  goToDashboard(): void {
    if (!this.gsUrl || this.gsUrl === 'undefined') {
      this.router.navigate([`${Urls.FUND}/${Urls.DASHBOARD}`]);
    } else {
      window.open(`${this.gsUrl}/${Urls.FUND}/${Urls.DASHBOARD}`, '_self');
    }
  }

  goHome(): void {
    let link = environment.fund.homeLink;
    if (this.paymentData.redirectLink && (!this.gsUrl || this.gsUrl === 'undefined')) {
      const redirectProtocol = new URL(this.paymentData.redirectLink).protocol;
      const redirectHostname = new URL(this.paymentData.redirectLink)?.hostname;
      link = this.paymentData.wasLoggedIn
        ? `${redirectProtocol}//${redirectHostname}/${Urls.FUND}/${Urls.DASHBOARD}`
        : this.homeLink || link;
    } else {
      if (!this.gsUrl || this.gsUrl === 'undefined') {
        link = this.homeLink || link;
      } else {
        link = this.paymentData.wasLoggedIn ? `${this.gsUrl}/${Urls.HOME}` : environment.fund.gsUrl;
      }
    }

    this.public ? window.open(link, '_self') : this.router.navigate([Urls.APP]);
  }

  displaySummaryPage(): boolean {
    if (
      environment.fund.environmentName === EnvironmentNames.CNCF &&
      this.paymentData.contributionType === CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE
    )
      return false;
    return [this.paymentStatus.PURCHASED, this.paymentStatus.ACTIVE].includes(
      this.paymentData.status
    );
  }

  getSummaryMessage(): string {
    switch (environment.fund.environmentName) {
      case EnvironmentNames.CNCF:
        return this.paymentData.contributionType === this.contributionTypes.BANK_TRANSFER_ONLINE
          ? 'SUMMARY_CONTRIBUTION_SUBMITTED_BT_ONLINE'
          : 'SUMMARY_CONTRIBUTION_SUBMITTED_CREDIT_CARD';
      case EnvironmentNames.SINNGEBER:
        switch (this.paymentData.contributionType) {
          case this.contributionTypes.CREDIT_CARD:
            return 'SUMMARY_CONTRIBUTION_SUBMITTED_CREDIT_CARD';
          case this.contributionTypes.SEPA_DEBIT:
            return 'SUMMARY_CONTRIBUTION_SUBMITTED_SEPA';
          default:
            return 'SUMMARY_CONTRIBUTION_SUBMITTED';
        }
      default:
        return 'SUMMARY_CONTRIBUTION_SUBMITTED';
    }
  }

  getCountryDetailsData(countryUuid: string): void {
    this.loadingCountryDetails = true;
    this.transactionService.getCountryDetails(countryUuid).subscribe(
      (countryDetails: SearchCountry) => {
        this.networkMemberData = countryDetails;
        this.loadingCountryDetails = false;
      },
      (error) => {
        console.error('Error fetching country details:', error);
        this.loadingCountryDetails = false;
      }
    );
  }

  get isGivelink(): boolean {
    if (!!this.customLink) return true;
    if (!this.customLink && !this.paymentData?.redirectLink) return false;
    const paramsId = new URL(this.paymentData.redirectLink).searchParams.get('id');
    return !!paramsId;
  }

  get networkMemberSuccessMessage(): string | null {
    return this.networkMemberData?.networkMember?.contriConfig?.successMessage || null;
  }

  get networkMemberRedirectUrl(): string | null {
    return this.networkMemberData?.networkMember?.contriConfig?.redirectUrl || null;
  }

  /**
   * Falls back to inferring the format from the message itself when the backend has not stored
   * one, so a formatted message still renders as formatted.
   */
  get networkMemberSuccessMessageFormat(): SUCCESS_MESSAGE_FORMAT {
    return resolveSuccessMessageFormat(
      this.networkMemberSuccessMessage,
      this.networkMemberData?.networkMember?.contriConfig?.successMessageFormat
    );
  }

  get networkMemberSuccessMessageIsRtf(): boolean {
    return this.networkMemberSuccessMessageFormat === SUCCESS_MESSAGE_FORMAT.RTF;
  }

  /**
   * A `||`-delimited message with **no stored format** predates the Plain text / RTF toggle: it was
   * authored in the old raw textarea and laid out here as body ‖ link line ‖ warning box, rendered
   * straight through `[innerHTML]`. Those messages routinely contain real markup — the "Click here"
   * segment is an anchor by design — so they keep the original rendering byte for byte.
   *
   * The stored-format condition is load-bearing, not a nicety: `||` is two ordinary characters in a
   * textarea with no markup validator, so keying only off it let an admin opt into raw-HTML rendering
   * on a public, unauthenticated page.
   */
  get networkMemberSuccessMessageIsLegacySegmented(): boolean {
    return isLegacySegmentedMessage(
      this.networkMemberSuccessMessage,
      this.networkMemberData?.networkMember?.contriConfig?.successMessageFormat
    );
  }

  /** Exposed so the template can pass the enum member to the richText pipe. */
  readonly SuccessMessageFormat = SUCCESS_MESSAGE_FORMAT;

  /**
   * AusGiving (Australia) offline contribution shown on the confirmation page:
   * Australia + tax deductible = Yes + amount below AUD 100,000.
   * At or above the ceiling the existing confirmation behavior is kept.
   * countryName originates from countries.ts ('Australia') while the component's
   * COUNTRY_NAMES is country-names.ts ('AUSTRALIA'), so compare case-insensitively.
   */
  get isAusgivingContribution(): boolean {
    return (
      this.environmentName === this.environments.SWISS &&
      this.paymentData?.taxDeduction === true &&
      this.paymentData?.countryName?.toUpperCase() === this.countryNames.AUSTRALIA &&
      Number(this.paymentData?.amount) < this.AUSGIVING_MAX_AMOUNT
    );
  }

  /**
   * Determines if the new credit card confirmation design should be displayed
   * Applies to credit card payments (including recurring) in all environments except CNCF
   */
  usesNewCreditCardDesign(): boolean {
    return (
      this.paymentData.contributionType === this.contributionTypes.CREDIT_CARD &&
      [
        this.paymentStatus.PURCHASED,
        this.paymentStatus.ACTIVE,
        this.paymentStatus.PENDING,
      ].includes(this.paymentData.status) &&
      this.environmentName !== this.environments.CNCF // Excludes network member scenarios
    );
  }

  private readonly specificErrorCodes = ['error.invalid_currency', 'error.invalid_country'];

  hasSpecificError(error: string): boolean {
    return this.specificErrorCodes.includes(error);
  }

  isPlainErrorMessage(error: string): boolean {
    if (!error) return false;
    // Snake case error codes are UPPER_SNAKE_CASE (only uppercase letters, digits, underscores)
    return !/^[A-Z0-9_]+$/.test(error);
  }

  get isErrorStatus(): boolean {
    return [this.paymentStatus.FAILED, this.paymentStatus.CANCELED].includes(
      this.paymentData?.status
    );
  }

  get fundNameForDisplay(): string {
    if (!this.paymentData) {
      return '';
    }

    const fundName = this.paymentData.fundName || this.paymentData.entityName;
    return this.fundNameFormatPipe.transform(fundName.trim());
  }

  private getCustomLink(): string {
    const queryParams = this.route.snapshot.queryParams;
    
    if (queryParams?.customLink) return queryParams.customLink;
    
    if (queryParams?.redirectLink) {
      const redirectUrl = new URL(queryParams.redirectLink);
      const idFromRedirect = redirectUrl?.searchParams.get('id');

      if (idFromRedirect) {
        this.customLinkFromRedirect = true;
        return idFromRedirect;
      }
    }

    return null;
  }
}
